import { segmentGraphemesFallback } from "@/lib/graphemes";
import { formatCodePoint } from "@/lib/tool-kit/text/code-points";
import {
  codePointLabel,
  GENERAL_CATEGORIES,
  type NameAliases,
  type UnicodeDatabase,
} from "@/lib/tool-kit/text/unicode-data";

/**
 * Everything the inspector says about a character that can be computed without the
 * character database: its encodings and escapes, how the text breaks into
 * user-perceived characters, and how the four normalization forms change it.
 */

const SURROGATE_FIRST = 0xd8_00;
const SURROGATE_LAST = 0xdf_ff;
const SUPPLEMENTARY = 0x1_00_00;

function isSurrogate(codePoint: number): boolean {
  return codePoint >= SURROGATE_FIRST && codePoint <= SURROGATE_LAST;
}

/** UTF-8 bytes of one code point; empty for a lone surrogate, which has none. */
export function utf8Bytes(codePoint: number): number[] {
  if (codePoint < 0x80) {
    return [codePoint];
  }
  if (codePoint < 0x8_00) {
    return [0xc0 + Math.floor(codePoint / 64), 0x80 + (codePoint % 64)];
  }
  if (codePoint < SUPPLEMENTARY) {
    if (isSurrogate(codePoint)) {
      return [];
    }
    return [
      0xe0 + Math.floor(codePoint / 4096),
      0x80 + (Math.floor(codePoint / 64) % 64),
      0x80 + (codePoint % 64),
    ];
  }
  return [
    0xf0 + Math.floor(codePoint / 262_144),
    0x80 + (Math.floor(codePoint / 4096) % 64),
    0x80 + (Math.floor(codePoint / 64) % 64),
    0x80 + (codePoint % 64),
  ];
}

/** UTF-16 code units of one code point: one, or a surrogate pair. */
export function utf16Units(codePoint: number): number[] {
  if (codePoint < SUPPLEMENTARY) {
    return [codePoint];
  }
  const offset = codePoint - SUPPLEMENTARY;
  return [
    SURROGATE_FIRST + Math.floor(offset / 1024),
    0xdc_00 + (offset % 1024),
  ];
}

function hex(value: number, digits: number): string {
  return value.toString(16).toUpperCase().padStart(digits, "0");
}

/** Bytes as "C3 A9". */
export function formatBytes(bytes: readonly number[]): string {
  return bytes.map((byte) => hex(byte, 2)).join(" ");
}

/** Code units as "D83D DE00". */
export function formatUnits(units: readonly number[]): string {
  return units.map((unit) => hex(unit, 4)).join(" ");
}

const URL_UNRESERVED = /^[A-Za-z0-9\-._~]$/;

export interface Escapes {
  /** `&#233;` */
  htmlDecimal: string;
  /** `&#xE9;` */
  htmlHex: string;
  /** `é`, or `\u{1F600}` beyond the Basic Multilingual Plane. */
  javascript: string;
  /** The surrogate-pair form `😀`, for code that predates `\u{…}`. */
  javascriptPair: string | null;
  /** `\E9` — CSS reads up to six hex digits. */
  css: string;
  /** Percent-encoded UTF-8 (RFC 3986); unreserved ASCII stays as it is. */
  url: string;
}

export function escapesFor(codePoint: number): Escapes {
  const character = String.fromCodePoint(codePoint);
  const astral = codePoint >= SUPPLEMENTARY;
  const bytes = utf8Bytes(codePoint);
  return {
    htmlDecimal: `&#${codePoint};`,
    htmlHex: `&#x${hex(codePoint, 1)};`,
    javascript: astral
      ? `\\u{${hex(codePoint, 1)}}`
      : `\\u${hex(codePoint, 4)}`,
    javascriptPair: astral
      ? utf16Units(codePoint)
          .map((unit) => `\\u${hex(unit, 4)}`)
          .join("")
      : null,
    css: `\\${hex(codePoint, 1)}`,
    url: URL_UNRESERVED.test(character)
      ? character
      : bytes.map((byte) => `%${hex(byte, 2)}`).join(""),
  };
}

/* ------------------------------------------------------------------ clusters */

export interface Cluster {
  text: string;
  /** UTF-16 offset in the input. */
  index: number;
  codePoints: number[];
}

function codePointsOf(text: string): number[] {
  return [...text].map((character) => character.codePointAt(0) ?? 0);
}

/**
 * Split text into grapheme clusters — user-perceived characters — without normalizing
 * it first, so that "é" typed as e + COMBINING ACUTE ACCENT stays two code points.
 * Uses `Intl.Segmenter`; without it, the site's fallback segmenter is used when the
 * text is already in NFC (where it changes nothing) and one cluster per code point
 * otherwise.
 */
export function graphemeClusters(text: string): Cluster[] {
  let segments: string[];
  if (typeof Intl.Segmenter === "function") {
    const segmenter = new Intl.Segmenter(undefined, {
      granularity: "grapheme",
    });
    segments = Array.from(segmenter.segment(text), ({ segment }) => segment);
  } else if (text.normalize("NFC") === text) {
    segments = segmentGraphemesFallback(text);
  } else {
    segments = [...text];
  }
  const clusters: Cluster[] = [];
  let index = 0;
  for (const segment of segments) {
    clusters.push({ text: segment, index, codePoints: codePointsOf(segment) });
    index += segment.length;
  }
  return clusters;
}

/* ------------------------------------------------------------- normalization */

export type NormalizationForm = "NFC" | "NFD" | "NFKC" | "NFKD";

export const NORMALIZATION_FORMS: readonly NormalizationForm[] = [
  "NFC",
  "NFD",
  "NFKC",
  "NFKD",
];

export interface TextSize {
  graphemes: number;
  codePoints: number;
  utf16: number;
  utf8: number;
}

/** UTF-8 length in bytes; a lone surrogate counts as the 3 bytes of U+FFFD. */
export function utf8Length(text: string): number {
  let bytes = 0;
  for (const character of text) {
    const length = utf8Bytes(character.codePointAt(0) ?? 0).length;
    bytes += length === 0 ? 3 : length;
  }
  return bytes;
}

export function textSize(text: string): TextSize {
  return {
    graphemes: graphemeClusters(text).length,
    codePoints: [...text].length,
    utf16: text.length,
    utf8: utf8Length(text),
  };
}

export interface NormalizationResult extends TextSize {
  form: NormalizationForm;
  text: string;
  /** True when the form leaves the text exactly as it was. */
  same: boolean;
}

/** The text in each normalization form, with its size and whether it changed. */
export function normalizationForms(text: string): NormalizationResult[] {
  return NORMALIZATION_FORMS.map((form) => {
    const normalized = text.normalize(form);
    return {
      form,
      text: normalized,
      same: normalized === text,
      ...textSize(normalized),
    };
  });
}

/* -------------------------------------------------------------------- details */

export interface CodePointDetails extends Escapes {
  codePoint: number;
  char: string;
  /** "U+00E9" */
  hex: string;
  utf8: number[];
  utf16: number[];
  /** The Unicode name, or a code point label such as `<control-000A>`; "" until the database loads. */
  name: string;
  /** True when `name` is a real Name property rather than a label. */
  named: boolean;
  aliases: NameAliases;
  /** Two-letter category, e.g. "Ll"; "" until the database loads. */
  category: string;
  categoryName: string;
  script: string;
  block: string;
  age: string;
  entities: readonly string[];
}

const PROPERTY_UNDERSCORE = /_/g;

/** "Old_Italic" → "Old Italic": property values are written with underscores. */
export function propertyLabel(value: string): string {
  return value.replace(PROPERTY_UNDERSCORE, " ");
}

/** Everything about one code point; database fields are empty while it loads. */
export function describeCodePoint(
  codePoint: number,
  database: UnicodeDatabase | null
): CodePointDetails {
  const category = database?.generalCategory(codePoint) ?? "";
  const name = database?.name(codePoint) ?? null;
  return {
    codePoint,
    char: String.fromCodePoint(codePoint),
    hex: formatCodePoint(codePoint),
    utf8: utf8Bytes(codePoint),
    utf16: utf16Units(codePoint),
    ...escapesFor(codePoint),
    name: name ?? (database ? codePointLabel(codePoint, category) : ""),
    named: name !== null,
    aliases: database?.aliases(codePoint) ?? {},
    category,
    categoryName: GENERAL_CATEGORIES[category] ?? "",
    script: database ? propertyLabel(database.script(codePoint)) : "",
    block: database ? propertyLabel(database.block(codePoint)) : "",
    age: database?.age(codePoint) ?? "",
    entities: database?.entities(codePoint) ?? [],
  };
}

const MARK = /^\p{M}$/u;
const INVISIBLE = /^[\p{C}\p{Z}]$/u;
export const DOTTED_CIRCLE = "◌";

/**
 * How to draw a code point on its own in a table: a combining mark on a dotted circle
 * (◌́), an invisible or space character as its abbreviation or code point, anything
 * else as itself. `symbolic` says the text is a stand-in rather than the character.
 */
export function displayGlyph(
  codePoint: number,
  aliases: NameAliases
): { text: string; symbolic: boolean } {
  const character = String.fromCodePoint(codePoint);
  if (MARK.test(character)) {
    return { text: `${DOTTED_CIRCLE}${character}`, symbolic: false };
  }
  if (INVISIBLE.test(character)) {
    return {
      text: aliases.abbreviation ?? formatCodePoint(codePoint),
      symbolic: true,
    };
  }
  return { text: character, symbolic: false };
}
